import React, { useState, useEffect } from "react";
import SelectionTable from "./common/SelectionTable";
import TabPanel from "./common/TabPanel";
import ParameterTable from "./common/ParameterTable";
import { parameterGroups } from "./OtherParameters"; // Import from OtherParameters

// Define tab configuration
const truckTabs = [
  { id: "electric", label: "Electric Truck Models" },
  { id: "diesel", label: "Diesel Truck Parameters" },
  { id: "life", label: "Truck Operational Life" }
];

const vehicleClassTabs = [
  { id: "Light Duty", label: "Light Duty (Class 1-3)" },
  { id: "Medium Duty", label: "Medium Duty (Class 4-6)" },
  { id: "Heavy Duty", label: "Heavy Duty (Class 7-8)" },
]

const TruckSelection = ({
  trucks,
  loading,
  selectedTrucks,
  setSelectedTrucks,
  truckCustomData,
  setTruckCustomData,
  otherParameters,
  setOtherParameters,
  nextStep,
  prevStep
}) => {
  const [editedIncentives, setEditedIncentives] = useState({});
  const [editedPrices, setEditedPrices] = useState({});
  const [activeTab, setActiveTab] = useState("electric"); // Default active tab
  const [activeVehicleClass, setActiveVehicleClass] = useState("Heavy Duty"); // Default active tab

  // Initialize or update truckCustomData when trucks or edited values change
  useEffect(() => {
    console.log('trucks', trucks);
    console.log('selectedTrucks', selectedTrucks);

    if (trucks.length > 0) {
      const updatedCustomData = { ...truckCustomData };

      // Update with any edited values
      trucks.forEach(truck => {
        const truckId = truck.truck_id;

        if (!updatedCustomData[truckId]) {
          updatedCustomData[truckId] = {
            price: truck.price,
            incentives: truck.incentives
          };
        }

        if (editedPrices.hasOwnProperty(truckId)) {
          updatedCustomData[truckId].price = editedPrices[truckId];
        }

        if (editedIncentives.hasOwnProperty(truckId)) {
          updatedCustomData[truckId].incentives = editedIncentives[truckId];
        }
      });

      setTruckCustomData(updatedCustomData);
    }
  }, [trucks, editedPrices, editedIncentives]);

  // Handle checkbox change
  const handleCheckboxChange = (truckIdOrArray, e) => {
    // Stop propagation if event is provided
    if (e) {
      e.stopPropagation();
    }

    setSelectedTrucks((prevSelected) => {
      // Handle array of IDs (for select all/none functionality)
      if (Array.isArray(truckIdOrArray)) {
        console.log('truckIdOrArray', truckIdOrArray);
        return truckIdOrArray;
      }
      // Handle single ID toggle
      const truckId = truckIdOrArray;
      if (prevSelected.includes(truckId)) {
        return prevSelected.filter((id) => id !== truckId);
      } else {
        return [...prevSelected, truckId];
      }
    });
    console.log('selectedTrucks', selectedTrucks);
  };

  // Fix the all selected checks
  const lightAllSelected = trucks.filter(truck => truck.truck_class === 'Light Duty')
    .every(truck => selectedTrucks.includes(truck.truck_id));
  const mediumAllSelected = trucks.filter(truck => truck.truck_class === 'Medium Duty')
    .every(truck => selectedTrucks.includes(truck.truck_id));
  const heavyAllSelected = trucks.filter(truck => truck.truck_class === 'Heavy Duty')
    .every(truck => selectedTrucks.includes(truck.truck_id));

  // Handle "Select All" checkbox
  const handleSelectAll = (e) => {
    if (e) e.stopPropagation();
    
    // Get current trucks for the active vehicle class
    const currentClassTrucks = trucks.filter(truck => truck.truck_class === activeVehicleClass);
    const currentClassTruckIds = currentClassTrucks.map(truck => truck.truck_id);
    
    setSelectedTrucks(prevSelected => {
      if (activeVehicleClass === 'Light Duty' && lightAllSelected) {
        // Deselect only Light Duty trucks, keep others
        return prevSelected.filter(id => !currentClassTruckIds.includes(id));
      } else if (activeVehicleClass === 'Medium Duty' && mediumAllSelected) {
        // Deselect only Medium Duty trucks, keep others
        return prevSelected.filter(id => !currentClassTruckIds.includes(id));
      } else if (activeVehicleClass === 'Heavy Duty' && heavyAllSelected) {
        // Deselect only Heavy Duty trucks, keep others
        return prevSelected.filter(id => !currentClassTruckIds.includes(id));
      } else {
        // Add all trucks of current class while keeping other selections
        const otherSelections = prevSelected.filter(id => !currentClassTruckIds.includes(id));
        return [...otherSelections, ...currentClassTruckIds];
      }
    });
  };

  // Incentive handling
  const handleIncentiveChange = (truckId, value) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    const incentiveValue = numericValue === '' ? 0 : parseInt(numericValue, 10);

    setEditedIncentives(prev => ({
      ...prev,
      [truckId]: incentiveValue
    }));
  };

  const getIncentiveValue = (truck) => {
    return editedIncentives.hasOwnProperty(truck.truck_id)
      ? editedIncentives[truck.truck_id]
      : truckCustomData[truck.truck_id]?.incentives || truck.incentives;
  };

  // Price handling
  const handlePriceChange = (truckId, value) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    const priceValue = numericValue === '' ? 0 : parseInt(numericValue, 10);

    setEditedPrices(prev => ({
      ...prev,
      [truckId]: priceValue
    }));
  };

  const getPriceValue = (truck) => {
    return editedPrices.hasOwnProperty(truck.truck_id)
      ? editedPrices[truck.truck_id]
      : truckCustomData[truck.truck_id]?.price || truck.price;
  };

  // Format currency with thousands separator and no decimals
  const formatCurrency = (value) => {
    return Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };
  
  // Define columns for the truck table
  const truckColumns = [
    { key: 'make', label: 'Make', width: '12.5%' },
    { key: 'model', label: 'Model', width: '12.5%' },
    { key: 'batt_cap', label: 'Battery (kWh)', width: '12.5%' },
    { key: 'operation_range', label: 'Range (miles)', width: '12.5%' },
    { key: 'price', label: 'MSRP', width: '15%' },
    { key: 'incentives', label: 'Incentives', width: '15%' },
    { key: 'is_available', label: 'Available', width: '12%' }
  ];

  // Define how to render each row
  const renderTruckRow = (truck) => (
    <>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">{truck.make}</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">{truck.model}</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{truck.batt_cap}</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{truck.operation_range.toFixed(2)}</td>
      <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        <div className="relative rounded-md shadow-sm">
          <div className="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center"><span className="text-gray-500 sm:text-sm">$</span></div>
          <input type="text" value={formatCurrency(getPriceValue(truck))} onChange={(e) => handlePriceChange(truck.truck_id, e.target.value)} className="w-full block pl-7 pr-2 py-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" />
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        <div className="relative rounded-md shadow-sm">
          <div className="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center"><span className="text-gray-500 sm:text-sm">$</span></div>
          <input type="text" value={formatCurrency(getIncentiveValue(truck))} onChange={(e) => handleIncentiveChange(truck.truck_id, e.target.value)} className="w-full block pl-7 pr-2 py-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" />
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm">
        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${truck.is_available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {truck.is_available ? "Yes" : "No"}
        </span>
      </td>
    </>
  );

  // Handle parameter changes
  const handleInputChange = (id, value) => {
    setOtherParameters(prev => ({
      ...prev,
      [id]: value,
    }));
  };

  if (loading) {
    return <div className="p-6">Loading trucks data...</div>;
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <TabPanel
        tabs={truckTabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      >
        {/* Tab 1: Electric Truck Selection */}
        <div>
          <p className="text-gray-600 mb-4">Select electric truck models and customize MSRPs and incentives as needed.</p>
          {trucks.length === 0 ? (
            <p>No trucks found. Please add trucks to continue.</p>
          ) : (
            <TabPanel
              tabs={vehicleClassTabs}
              activeTab={activeVehicleClass}
              onTabChange={setActiveVehicleClass}
            >
              {/* Tab 1: Light Duty Trucks */}
              <div>
                <SelectionTable
                  items={trucks.filter(truck => truck.truck_class === 'Light Duty')}
                  selectedItems={selectedTrucks}
                  handleCheckboxChange={handleCheckboxChange}
                  handleSelectAll={handleSelectAll}
                  allSelected={lightAllSelected}
                  columns={truckColumns}
                  itemIdKey="truck_id"
                  renderRow={renderTruckRow}
                  showActionColumn={false}
                />
              </div>

              {/* Tab 2: Medium Duty Trucks */}
              <div>
                <SelectionTable
                  items={trucks.filter(truck => truck.truck_class === 'Medium Duty')}
                  selectedItems={selectedTrucks}
                  handleCheckboxChange={handleCheckboxChange}
                  handleSelectAll={handleSelectAll}
                  allSelected={mediumAllSelected}
                  columns={truckColumns}
                  itemIdKey="truck_id"
                  renderRow={renderTruckRow}
                  showActionColumn={false}
                />
              </div>

              {/* Tab 3: Heavy Duty Trucks */}
              <div>
                <SelectionTable
                  items={trucks.filter(truck => truck.truck_class === 'Heavy Duty')}
                  selectedItems={selectedTrucks}
                  handleCheckboxChange={handleCheckboxChange}
                  handleSelectAll={handleSelectAll}
                  allSelected={heavyAllSelected}
                  columns={truckColumns}
                  itemIdKey="truck_id"
                  renderRow={renderTruckRow}
                  showActionColumn={false}
                />
              </div>
            </TabPanel>
          )}
        </div>

        {/* Tab 2: Diesel Truck Parameters */}
        <div>
          <p className="text-gray-600 mb-4">Configure diesel truck price and its average MPG.</p>
          <ParameterTable
            parameters={parameterGroups.dieselTruckCost.inputs}
            values={otherParameters}
            onValueChange={handleInputChange}
            formatValue={formatCurrency}
          />
        </div>

        {/* Tab 3: Truck Operational Life */}
        <div>
          <p className="text-gray-600 mb-4">Configure expected lifespan of trucks.</p>
          <ParameterTable
            parameters={parameterGroups.truckLife.inputs}
            values={otherParameters}
            onValueChange={handleInputChange}
          />
        </div>
      </TabPanel>
    </div>
  );
};

export default TruckSelection; 